import { useEffect } from "react";
import { useWorkspaceBranding } from "@/hooks/use-workspace-branding";
import {
  clearPersistedBranding,
  writePersistedBranding,
} from "@/lib/workspace-branding-storage";
import {
  applyWorkspaceAccent,
  clearWorkspaceAccent,
  registerAccentOwner,
} from "@/lib/workspace-branding";

/**
 * THE single owner of the workspace accent CSS variables. Mounted exactly once,
 * inside WorkspaceProvider. Nothing else in the app may write those variables
 * (enforced by an ESLint rule and by registerAccentOwner in dev).
 *
 * Keeping the unmount cleanup is correct now that there is one owner: this
 * component unmounting means the operator left the authenticated tree, which is
 * exactly when branding should revert. Previously a SECOND owner's unmount fired
 * the same cleanup on every navigation to a page without AppLayout, which is
 * what made branding look intermittent.
 *
 * The cleanup deliberately does not clear the PERSISTED record: that record is
 * what removes the indigo flash on the next load, and it is invalidated by the
 * active-workspace id rather than by a route change.
 *
 * Renders nothing.
 */
export function WorkspaceAccentController(): null {
  const { accent, workspaceId, displayName } = useWorkspaceBranding();

  useEffect(() => registerAccentOwner(), []);

  useEffect(() => {
    if (accent && workspaceId) {
      applyWorkspaceAccent(accent);
      writePersistedBranding({
        workspaceId,
        accent: accent.accent,
        foreground: accent.foreground,
        name: displayName || undefined,
      });
    } else {
      clearWorkspaceAccent();
      clearPersistedBranding();
    }
    return () => clearWorkspaceAccent();
  }, [accent, workspaceId, displayName]);

  return null;
}
